import React, { useEffect, useRef, useState } from 'react';
import { FaChevronRight } from 'react-icons/fa';

const HEIGHT = 72;
const THUMB = 68;

/**
 * Slide-to-confirm control — the web mirror of the app's parking swipe gate.
 * Drag the red thumb to the right edge to fire onConfirm (used to mark a
 * parking card used at the entrance). Shows a spinner in the thumb while the
 * confirm action runs; resets to the start if it fails.
 * @component
 */
const SwipeToConfirm = ({ label = 'Nur für SCB-Personal!', loading = false, onConfirm }) => {
  const trackRef = useRef(null);
  const draggingRef = useRef(false);
  const startXRef = useRef(0);
  const dxRef = useRef(0);
  const [dx, setDx] = useState(0);

  const maxDx = () => {
    const w = trackRef.current ? trackRef.current.offsetWidth : 0;
    return Math.max(0, w - THUMB - 4);
  };

  const setClamped = (val) => {
    const clamped = Math.max(0, Math.min(maxDx(), val));
    dxRef.current = clamped;
    setDx(clamped);
  };

  const onDown = (clientX) => {
    if (loading) return;
    draggingRef.current = true;
    startXRef.current = clientX - dxRef.current;
  };

  useEffect(() => {
    const move = (e) => {
      if (!draggingRef.current) return;
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      setClamped(clientX - startXRef.current);
    };
    const up = () => {
      if (!draggingRef.current) return;
      draggingRef.current = false;
      if (dxRef.current >= maxDx() - 8) {
        setClamped(maxDx());
        onConfirm();
      } else {
        setClamped(0);
      }
    };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
    window.addEventListener('touchmove', move);
    window.addEventListener('touchend', up);
    return () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
      window.removeEventListener('touchmove', move);
      window.removeEventListener('touchend', up);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, onConfirm]);

  // Once the confirm action settles (e.g. failed), snap the thumb back so the
  // user can retry from the start.
  useEffect(() => {
    if (!loading && !draggingRef.current) setClamped(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  return (
    <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 10 }}>
      <style>{'@keyframes scb-swipe-spin{to{transform:rotate(360deg)}}'}</style>
      <div
        ref={trackRef}
        style={{
          position: 'relative',
          width: '100%',
          maxWidth: 360,
          height: HEIGHT,
          background: '#e6e6e6',
          borderRadius: HEIGHT / 2,
          overflow: 'hidden',
          userSelect: 'none',
          touchAction: 'none',
        }}
      >
        <span
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#c8102e',
            fontWeight: 'bold',
            fontSize: 15,
            pointerEvents: 'none',
            opacity: Math.max(0, 1 - dx / (maxDx() || 1)),
          }}
        >
          {label}
        </span>

        {/* Red trail that follows the thumb and covers the track (and label)
            behind it as the user swipes, so nothing shows underneath. */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            height: '100%',
            width: dx + THUMB + 2,
            background: '#c8102e',
            pointerEvents: 'none',
          }}
        />

        <div
          onMouseDown={(e) => onDown(e.clientX)}
          onTouchStart={(e) => onDown(e.touches[0].clientX)}
          style={{
            position: 'absolute',
            top: 2,
            left: 2,
            transform: `translateX(${dx}px)`,
            width: THUMB,
            height: THUMB,
            borderRadius: '50%',
            background: '#c8102e',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            cursor: loading ? 'default' : 'grab',
          }}
        >
          {loading ? (
            <span
              style={{
                width: 22,
                height: 22,
                border: '3px solid rgba(255,255,255,0.4)',
                borderTopColor: '#fff',
                borderRadius: '50%',
                animation: 'scb-swipe-spin 0.8s linear infinite',
              }}
            />
          ) : (
            <FaChevronRight size={22} />
          )}
        </div>
      </div>
    </div>
  );
};

export default SwipeToConfirm;
